import { Directive } from "@app/components/directive";
import { IFieldController } from "@app/components/kb-field/kb-field.component";

export interface IInputScope extends ng.IScope {
    ngModel: any;
    /** pass through ngChange capability */
    ngChange?: () => void;
    /** pass through for ngDisabled */
    ngDisabled: boolean;
    ngModelOptions: any;

    /** when one of our directives is used as a child of another, we probably want it to ignore the parent KbField
     *  (like checkboxes in an image multi-select).  Otherwise, the checkbox will find the parent field and
     *  mess with it's settings when it shouldn't.  To stop this, set ignoreField to true.
     */
    ignoreField?: boolean;

    /** called once there has been a blur event to track whether the user has interacted with the control */
    touched: () => void;

    _ngChange: () => void;
    _setHasValue: (hasValue: boolean) => void;

    textColor?: string;
    backgroundColor?: string;
}

/**
 * Acts as a base class to directives that work together with kbField to manage focus and common setup
 */
export class KbInput extends Directive {
    constructor(public $timeout: ng.ITimeoutService) {
        super();
        this.restrict = "E";
        this.replace = true;
        this.scope = {
            ngModel: "=",
            ngDisabled: "=?",
            ngModelOptions: "=?",
            ngChange: "&",
            ngBlur: "&",
            touched: "&",
            ignoreField: "=?",
            textColor: "=?",
            backgroundColor: "=?",
            heightOverride: "=?"
        }

        this.require = ["ngModel", "?^^kbField"];
        this.link = (s, e, a, c) => this.kbInputLinkFn(s as IInputScope, e, a, c);
    }

    public kbInputLinkFn(scope: IInputScope, element: ng.IAugmentedJQuery, atts: ng.IAttributes, controllers: any[], shouldFloat: boolean = true, isEmpty?: (val: any) => boolean) {
        let ngModelCtrl = controllers[0] as ng.INgModelController;
        let kbField: IFieldController = null;
        if (!scope.ignoreField) {
            kbField = controllers.length > 1 ? controllers.last() : null;
        }

        if (kbField) { //register our input ngModel with kbField
            kbField.setShouldFloat(shouldFloat);
        }

        if (!isEmpty) {
            isEmpty = (val) => {
                return (val == null || val.toString().length == 0);
            };
        }

        let ngModelPipelineCheckValue = (val) => {
            if (kbField) kbField.setHasValue(!isEmpty(val) && !(Array.isArray(val) && !val.length));
            return val;
        }

        ngModelCtrl.$parsers.push(ngModelPipelineCheckValue);
        ngModelCtrl.$formatters.push(ngModelPipelineCheckValue);
        ngModelCtrl.$viewChangeListeners.push(() => {
            //for some controls, blur is not the right time to consider whether the control has been touched (like select controls).
            //so we also track it here.
            if (scope.touched) {
                scope.touched();
            }
        });


        let focusin = (e: JQueryEventObject) => {
            if (kbField) kbField.setFocused(true);
        };
        let focusout = () => {
            if (kbField) {
                kbField.setFocused(false);
            }
            if (scope.touched) {
                scope.touched();
            }
        }
        element.on("focusin", focusin);
        element.on("focusout", focusout);

        // for some reason the ngChange is getting run before the model in the parent controller
        // is being updated by the value change, so we need to do this trick to wait until
        // everything is done before firing the ng-change
        scope._ngChange = () => {
            this.$timeout(() => {
                scope.ngChange();
            }, 0);
        };

        // bind keyboard event: esc(27)
        let el = element;
        if (!el.is("input")) {
            el = el.find("input");
        }
        if (el && el.length > 0) {
            el.on("keydown", (e: JQueryEventObject) => {
                if (e.which == 27) {
                    el.blur();
                }
            });
        }

        scope._setHasValue = (hasValue) => {
            if (kbField) {
                kbField.setHasValue(hasValue);
            }
        };

        scope.$on("$destroy", () => {
            if (kbField) {
                kbField.setFocused(false);
                kbField.setHasValue(false);
            }
            element.off();
        });
    }
}
